/* eslint-disable react/no-unused-prop-types */
/* eslint-disable max-len */
import type { HTMLAttributes } from 'react';
import React from 'react';
import classNames from 'classnames';
import Grid from '$components/layout/Grid';
import { useAppContext } from '$util/AppContext';
import { getImageByKey, getShortTextByKey, renderRichTextByKey } from '$cms/getDataByKey';
import type { CmsContentProps } from '$cms/index';
import { changeUrl } from '$util/index';
import { useModalQueue } from '$util/hooks/useModalQueue';
import { SimpleLink } from '@/components/atoms/SimpleLink';
import { Title } from '@/components/atoms/Title';
import COVIDModal from '../COVIDModal';
import type { ITile } from './Tile';
import Tile from './Tile';
import Styled, { DotLineStyled } from './styled';

const { Row, Col } = Grid;

export interface ReassuranceProps extends HTMLAttributes<HTMLDivElement> {
  readonly theme?:
    | 'light'
    | 'blueIcons'
    | 'blueIconsV2'
    | 'blueIconCarServiceMakeExperiment'
    | 'blueIconMotExperiment';
  readonly subTitle?: React.ReactNode;
  readonly showDotLines?: boolean;
  readonly images?: string[];
  readonly carMake?: string;
}

interface IComponentVersionProps extends HTMLAttributes<HTMLDivElement> {
  subTitle?: React.ReactNode;
  carMake?: string;
  toggleModal?: () => void;
}

const sharedCopy = new Map<string, string>();

sharedCopy.set(
  'default.100Online',
  'Get fair & transparent upfront prices for all makes and models, and book in a few taps only'
);
sharedCopy.set(
  'default.expertMechanics',
  'We personally select and vet only the best local garages, fully covered by a 12-month warranty on parts and labour'
);
sharedCopy.set(
  'default.collectionToDoor',
  'Our fully insured Fixter Heroes collect and deliver to your door, with flexible time slots that suit you'
);
sharedCopy.set(
  'default.inHouseExperts',
  'Our friendly team of specialists will support you every step of the way and take care of your car from start to finish'
);

function DefaultVersion({ subTitle }: IComponentVersionProps) {
  const tiles: ITile[] = [
    {
      title: '100% online',
      img: '/reassurance-section/icon_online_vpcxbn.svg',
      children: sharedCopy.get('default.100Online'),
    },
    {
      title: 'Expert mechanics',
      img: '/reassurance-section/icon_mechanics_xshfpq.svg',
      children: sharedCopy.get('default.expertMechanics'),
    },
    {
      title: 'Collection to your door',
      img: '/reassurance-section/icon_collection_z7vcke.svg',
      children: sharedCopy.get('default.collectionToDoor'),
    },
    {
      title: 'In-house expert advisors',
      img: '/reassurance-section/icon_advisors_u1db6j.svg',
      children: sharedCopy.get('default.inHouseExperts'),
    },
  ];
  return (
    <>
      <Title>Car care you&apos;ll love</Title>
      {subTitle && <Title h={5}>{subTitle}</Title>}
      <Row>
        {tiles.map((tile) => (
          <Col xs={6} lg={3} key={tile.title}>
            <Tile title={tile.title} img={tile.img}>
              {tile.children}
            </Tile>
          </Col>
        ))}
      </Row>
    </>
  );
}

function LightVersion({ subTitle }: IComponentVersionProps) {
  const tiles: ITile[] = [
    {
      title: '100% online',
      img: '/reassurance-section/icon_online_light_ivrpep.svg',
      children: sharedCopy.get('default.100Online'),
    },
    {
      title: 'Expert mechanics',
      img: '/reassurance-section/icon_mechanics_light_tsyc4z.svg',
      children: sharedCopy.get('default.expertMechanics'),
    },
    {
      title: 'Collection to your door',
      img: '/reassurance-section/icon_collection_light_ts5em4.svg',
      children: sharedCopy.get('default.collectionToDoor'),
    },
    {
      title: 'In-house expert advisors',
      img: '/reassurance-section/icon_advisors_light_areao8.svg',
      children: sharedCopy.get('default.inHouseExperts'),
    },
  ];
  return (
    <>
      <Title>Car care you&apos;ll love</Title>
      {subTitle && <Title h={5}>{subTitle}</Title>}
      <Row>
        {tiles.map((tile) => (
          <Col xs={6} lg={3} key={tile.title}>
            <Tile title={tile.title} img={tile.img}>
              {tile.children}
            </Tile>
          </Col>
        ))}
      </Row>
    </>
  );
}

const getBlueTiles = (toggleModal: IComponentVersionProps['toggleModal']) => [
  {
    title: 'Fair pricing',
    img: '/reassurance-section/icon_fair-pricing_rhsirm.svg',
    children:
      'We negotiate competitive rates with garages to offer the best prices, on average 30% cheaper than main dealerships.',
  },
  {
    title: 'Vetted garages',
    img: '/reassurance-section/icon_vetted-garages_en4oim.svg',
    children:
      'We personally select and vet only the best local garages, fully covered by a 12-month warranty on parts and labour.',
  },
  {
    title: 'Collection from your door',
    img: '/reassurance-section/icon_home_dcjlhh.svg',
    children: (
      <>
        Our fully insured Fixter Heroes offer a{' '}
        <SimpleLink
          type={1}
          text="contact-free"
          onClick={(event) => {
            event?.preventDefault();
            if (toggleModal) {
              toggleModal();
            }
          }}
        />
        collection and delivery service, with flexible time slots that suit you.
      </>
    ),
  },
  {
    title: 'Advisors on your side',
    img: '/reassurance-section/icon_advisors-blue_vlfae7.svg',
    children:
      'Our in-house experts make sure any quotes you receive are relevant and fairly priced, so no nasty surprises!',
  },
];

function BlueVersion({ subTitle, toggleModal }: IComponentVersionProps) {
  const {
    cmsContent: { dictionary },
  } = useAppContext<CmsContentProps>();

  const getReassuranceImage = getImageByKey.bind(dictionary);
  const getReassuranceShortText = getShortTextByKey.bind(dictionary);
  const getReassuranceDescription = renderRichTextByKey.bind(dictionary);

  const mappedData = [
    'reassurance.section1',
    'reassurance.section2',
    'reassurance.section3',
    'reassurance.section4',
  ];

  const fallbacks = [
    {
      title: 'Fair pricing',
      img: '/reassurance-section/icon_fair-pricing_rhsirm.svg',
      children: (
        <>
          We negotiate competitive rates with garages to offer the best prices, on average 30% cheaper than
          main dealerships.
        </>
      ),
    },
    {
      title: 'Vetted garages',
      img: '/reassurance-section/icon_vetted-garages_en4oim.svg',
      children: (
        <>
          We personally select and vet only the best local garages, fully covered by a 12-month warranty on
          parts and labour.
        </>
      ),
    },
    {
      title: 'Collection from your door',
      img: '/reassurance-section/icon_home_dcjlhh.svg',
      children: (
        <>
          Our fully insured Fixter Heroes offer a{' '}
          <SimpleLink
            type={1}
            text="contact-free"
            onClick={(event) => {
              event?.preventDefault();
              if (toggleModal) {
                toggleModal();
              }
            }}
          />
          collection and delivery service, with flexible time slots that suit you.
        </>
      ),
    },
    {
      title: 'Advisors on your side',
      img: '/reassurance-section/icon_advisors-blue_vlfae7.svg',
      children: (
        <>
          Our in-house experts make sure any quotes you receive are relevant and fairly priced, so no nasty
          surprises!
        </>
      ),
    },
  ];
  return (
    <>
      <Title>
        {getReassuranceShortText('reassurance.text.title', "Simply book online and we'll handle the rest")}
      </Title>
      {subTitle && <Title h={5}>{subTitle}</Title>}
      <Row>
        {mappedData.map((idPrefix, idx) => (
          <Col xs={12} lg={3} key={getReassuranceShortText(`${idPrefix}.title`, fallbacks[idx].title)}>
            <Tile
              title={getReassuranceShortText(`${idPrefix}.title`, fallbacks[idx].title)}
              img={
                getReassuranceImage(`${idPrefix}.image`, {
                  img: { original_secure_url: fallbacks[idx].img, width: 64, height: 64 },
                  alt: '',
                  title: '',
                }).img.original_secure_url
              }
            >
              <div
                onClickCapture={(e) => {
                  e.preventDefault();
                  const { target } = e;
                  const { hash, href } = target as HTMLAnchorElement;
                  if (!href) return e;
                  if (hash !== '#modal') {
                    return changeUrl({ pathname: href });
                  }
                  if (toggleModal) toggleModal();
                  return e;
                }}
              >
                {getReassuranceDescription(`${idPrefix}.description`, fallbacks[idx].children)}
              </div>
            </Tile>
          </Col>
        ))}
      </Row>
    </>
  );
}

function BlueVersion2({ subTitle, toggleModal }: IComponentVersionProps) {
  const tiles = getBlueTiles(toggleModal);
  return (
    <>
      <Title>Simply book online and we&apos;ll handle the rest</Title>
      {subTitle && <Title h={5}>{subTitle}</Title>}
      <Row>
        {tiles.map((tile) => (
          <Col xs={12} lg={12} key={tile.title}>
            <Tile title={tile.title} img={tile.img}>
              {tile.children}
            </Tile>
          </Col>
        ))}
      </Row>
    </>
  );
}

function BlueVersionCarServiceMakeExperiment({ carMake }: IComponentVersionProps) {
  const tiles = [
    {
      title: `30% cheaper than ${carMake} dealerships`,
      img: '/reassurance-section/icon_fair-pricing_rhsirm.svg',
      children:
        'Our preferential rates with garages mean 30% cheaper servicing on average, without compromising on quality.',
    },
    {
      title: 'Only the best garages',
      img: '/reassurance-section/icon_car_g1xykz.svg',
      children:
        'Our rigorous selection criteria means only 1 in 20 garages pass our test. Wave goodbye to anxious googling!',
    },
    {
      title: `Your ${carMake} warranty, protected`,
      img: '/reassurance-section/icon_shield_n3wplz.svg',
      children: `We use ${carMake}-certified parts only, so your car's warranty is protected—and you're covered by a Fixter 12 month warranty, too.`,
    },
    {
      title: 'Our in-house experts, by your side',
      img: '/reassurance-section/icon_advisors-blue_vlfae7.svg',
      children:
        'Our team of qualified mechanics are available to help you from start to finish, in plain English.',
    },
  ];
  return (
    <>
      <Title>{carMake}-approved servicing, the smarter way</Title>
      <Row>
        {tiles.map((tile) => (
          <Col xs={12} lg={3} key={tile.title}>
            <Tile title={tile.title} img={tile.img}>
              {tile.children}
            </Tile>
          </Col>
        ))}
      </Row>
    </>
  );
}

function BlueVersionMotExperiment() {
  const tiles = [
    {
      title: 'Free same-day collection and return',
      img: '/reassurance-section/icon_car_g1xykz.svg',
      children:
        'From your home, office, or anywhere else, at a time that works for you—so no need to take time out of your day.',
    },
    {
      title: 'Vetted garages you can trust',
      img: '/reassurance-section/icon_vetted-garages_en4oim.svg',
      children:
        'Our rigorous selection criteria means only 1 in 20 garages pass our test. Wave goodbye to anxious googling!',
    },
    {
      title: 'Double warranty-protected',
      img: '/reassurance-section/icon_shield_n3wplz.svg',
      children:
        "Any work needed to pass your MOT uses manufacturer-certified parts only, so your car's warranty is untouched—and you're covered by a Fixter 12 month warranty, too.",
    },
    {
      title: 'Our in-house experts, by your side',
      img: '/reassurance-section/icon_advisors-blue_vlfae7.svg',
      children:
        'Our team of qualified mechanics are available to help you from start to finish, in plain English.',
    },
  ];
  return (
    <>
      <Title>Hassle-free, without compromising on quality</Title>
      <Row>
        {tiles.map((tile) => (
          <Col xs={12} lg={3} key={tile.title}>
            <Tile title={tile.title} img={tile.img}>
              {tile.children}
            </Tile>
          </Col>
        ))}
      </Row>
    </>
  );
}

const themeVersionLookup = {
  light: LightVersion,
  blueIcons: BlueVersion,
  blueIconsV2: BlueVersion2,
  blueIconCarServiceMakeExperiment: BlueVersionCarServiceMakeExperiment,
  blueIconMotExperiment: BlueVersionMotExperiment,
  default: DefaultVersion,
};

/**
 * @deprecated to be dropped or replaced in upcoming design system; avoid used
 */
function ReassuranceSection({
  theme = 'light',
  subTitle,
  showDotLines = true,
  images,
  carMake,
  className,
  ...props
}: ReassuranceProps): JSX.Element {
  const [, queueModal] = useModalQueue();
  const ComponentVersion = themeVersionLookup[theme] || themeVersionLookup.default;

  const queueCovidModal = () => {
    // eslint-disable-next-line react/jsx-key
    queueModal([<COVIDModal images={images || []} />, 'modal']);
  };

  // CMS Prototyping
  // const { cmsContent } = useContext(AppContext);

  /**
   * @note
   * @todo This is being called multiple times - must investigate
   */

  /**
   * @todo: Explicit type for reassurance data
   */

  return (
    <>
      {showDotLines && (
        <>
          <DotLineStyled className="top-dots" size="small" colour="blue" />
          <DotLineStyled className="middle-dots" size="small" colour="blue" />
          <DotLineStyled className="bottom-dots" size="small" colour="blue" />
        </>
      )}
      <Styled {...props} className={classNames('ReassuranceSection', `-${theme}`, className)}>
        <ComponentVersion subTitle={subTitle} toggleModal={queueCovidModal} carMake={carMake} />
      </Styled>
    </>
  );
}

ReassuranceSection.displayName = 'Reassurance';

export default ReassuranceSection;
